// frontend/src/pages/AvisosPage.js
import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';
import { 
    Bell, PlusCircle, Edit, Trash2, X, Users, CheckCircle, Clock, 
    Power, PowerOff, AlertTriangle, Info, MessageSquare, History, Zap, Calendar 
} from 'lucide-react';

const TIPO_AVISO_CLASSES = {
    info: 'bg-blue-100 border-blue-500 text-blue-800',
    warning: 'bg-yellow-100 border-yellow-500 text-yellow-800',
    error: 'bg-red-100 border-red-500 text-red-800',
    success: 'bg-green-100 border-green-500 text-green-800',
};

const TIPO_AVISO_ICONE = {
    info: <Info className="text-blue-500" size={20} />,
    warning: <AlertTriangle className="text-yellow-500" size={20} />,
    error: <AlertTriangle className="text-red-500" size={20} />,
    success: <CheckCircle className="text-green-500" size={20} />,
};

// --- CONFIGURAÇÃO DE GATILHOS E MENSAGENS PADRÃO ---
const TRIGGER_TEMPLATES = {
    'new_schedule': {
        label: 'Ao criar um agendamento',
        title: 'Novo Agendamento',
        defaultMessage: 'O cliente {client_name} foi agendado para {scheduled_date} às {scheduled_time}.',
        variables: '{client_name}, {scheduled_date}, {scheduled_time}'
    },
    'tracking_release': {
        label: 'Ao liberar inclusão (Rastreio)',
        title: 'Inclusão de Rastreio Liberada',
        defaultMessage: 'A inclusão de rastreio foi liberada para a OS #{os_number}. Cliente: {cliente_name}.',
        variables: '{os_number}, {cliente_name}, {data_emissao}'
    },
    'billing_release': {
        label: 'Ao liberar faturamento (Laudos)',
        title: 'Faturamento Liberado',
        defaultMessage: 'O faturamento foi liberado para a OS #{os_number}. Cliente: {cliente_name}.',
        variables: '{os_number}, {cliente_name}, {data_emissao}'
    },
    'invoice_created': {
        label: 'Ao faturar OS (Geral)',
        title: 'OS Faturada',
        defaultMessage: 'A OS #{os_number} foi faturada com sucesso. Cliente: {cliente_name}.',
        variables: '{os_number}, {cliente_name}'
    },
    'receivable_sent': {
        label: 'Ao enviar conta a receber (Status Enviado)',
        title: 'Fatura Enviada',
        defaultMessage: 'A conta da OS #{os_number} (NF {nf_numero}) foi enviada em {data_envio}. Valor: R$ {valor_total}.',
        variables: '{os_number}, {nf_numero}, {data_envio}, {cliente_name}, {valor_total}'
    },
    // --- GATILHOS FINANCEIROS ---
    'finance_payable_due': {
        label: 'A Pagar: Vence em X dias',
        title: 'Aviso de Vencimento (A Pagar)',
        defaultMessage: 'Lembrete: A conta "{descricao}" ({fornecedor}) vence em {vencimento}. Valor: R$ {valor}.',
        variables: '{descricao}, {fornecedor}, {vencimento}, {valor}',
        hasDaysInput: true 
    },
    'finance_payable_overdue': {
        label: 'A Pagar: Vencido (Ontem)',
        title: 'Conta Vencida (A Pagar)',
        defaultMessage: 'Alerta: A conta "{descricao}" ({fornecedor}) venceu ontem ({vencimento}). Valor: R$ {valor}.',
        variables: '{descricao}, {fornecedor}, {vencimento}, {valor}'
    },
    'finance_receivable_due': {
        label: 'A Receber: Vence em X dias',
        title: 'Aviso de Vencimento (A Receber)',
        defaultMessage: 'Lembrete: A fatura "{descricao}" do cliente {cliente} vence em {vencimento}. Valor: R$ {valor}.',
        variables: '{descricao}, {cliente}, {vencimento}, {valor}',
        hasDaysInput: true
    },
    'finance_receivable_overdue': {
        label: 'A Receber: Vencido (Ontem)',
        title: 'Conta Vencida (A Receber)',
        defaultMessage: 'Alerta: A fatura "{descricao}" do cliente {cliente} venceu ontem ({vencimento}) e ainda não foi paga.',
        variables: '{descricao}, {cliente}, {vencimento}, {valor}'
    }
};

// --- Modal: Formulário de Aviso/Template ---
const AvisoFormModal = ({ isTemplate, avisoToEdit, onClose, onSave }) => {
    const { apiFetch } = useApi();
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [type, setType] = useState('info');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [triggerEvent, setTriggerEvent] = useState('');
    const [daysBefore, setDaysBefore] = useState(0); 
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const isSystemTemplate = isTemplate && avisoToEdit && avisoToEdit.is_system === 1;

    useEffect(() => {
        if (avisoToEdit) {
            setTitle(avisoToEdit.title || '');
            setMessage(avisoToEdit.message || '');
            setType(avisoToEdit.type || 'info');
            setTriggerEvent(avisoToEdit.trigger_event || '');
            setDaysBefore(avisoToEdit.days_before || 0); 
            
            if (!isTemplate) {
                setStartDate(avisoToEdit.start_date ? avisoToEdit.start_date.split('T')[0] : '');
                setEndDate(avisoToEdit.end_date ? avisoToEdit.end_date.split('T')[0] : '');
            }
        }
    }, [avisoToEdit, isTemplate]);

    // Função que preenche a mensagem automaticamente ao escolher o gatilho
    const handleTriggerChange = (e) => {
        const selectedTrigger = e.target.value;
        setTriggerEvent(selectedTrigger);

        if (selectedTrigger && !TRIGGER_TEMPLATES[selectedTrigger]?.hasDaysInput) {
            setDaysBefore(0);
        }

        if (selectedTrigger && TRIGGER_TEMPLATES[selectedTrigger]) {
            const def = TRIGGER_TEMPLATES[selectedTrigger];
            if (!message || window.confirm("Deseja substituir o título e a mensagem pelos padrões deste gatilho?")) {
                setMessage(def.defaultMessage);
                if (!title) setTitle(def.title || def.label);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const url = isTemplate
            ? (avisoToEdit ? `/avisos/templates/${avisoToEdit.id}` : '/avisos/templates')
            : (avisoToEdit ? `/avisos/${avisoToEdit.id}` : '/avisos');
        
        const method = avisoToEdit ? 'PUT' : 'POST';

        const body = {
            title,
            message,
            type,
            start_date: isTemplate ? null : (startDate || null),
            end_date: isTemplate ? null : (endDate || null),
            is_active: avisoToEdit ? avisoToEdit.is_active : 0,
            trigger_event: isTemplate ? triggerEvent : null,
            days_before: isTemplate ? parseInt(daysBefore) : 0 
        };

        try {
            await apiFetch(url, { method, body });
            onSave();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const selectedTemplateConfig = triggerEvent ? TRIGGER_TEMPLATES[triggerEvent] : null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold">{avisoToEdit ? 'Editar' : 'Criar'} {isTemplate ? 'Modelo' : 'Aviso'}</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                        {error && <p className="p-2 text-sm text-red-700 bg-red-100 rounded">{error}</p>}
                        {isSystemTemplate && (
                            <div className="p-3 text-sm text-blue-700 bg-blue-100 rounded-md flex items-center gap-2">
                                <Info size={16} /> Este é um modelo de sistema. Apenas a mensagem e o tipo podem ser editados.
                            </div>
                        )}
                        
                        {/* Seletor de Gatilho (Apenas para Templates) */}
                        {isTemplate && (
                            <div className="bg-purple-50 p-3 rounded-md border border-purple-100 space-y-3">
                                <div>
                                    <label className="text-sm font-bold text-purple-800 flex items-center gap-2 mb-1">
                                        <Zap size={16} /> Disparar Automaticamente quando:
                                    </label>
                                    <select 
                                        value={triggerEvent} 
                                        onChange={handleTriggerChange} 
                                        className="w-full p-2 border rounded border-purple-200 text-purple-900 font-medium"
                                        disabled={isSystemTemplate}
                                    >
                                        <option value="">Sem Gatilho (Envio Manual)</option>
                                        {Object.entries(TRIGGER_TEMPLATES).map(([key, template]) => (
                                            <option key={key} value={key}>{template.label}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Campo de Dias de Antecedência (Condicional) */}
                                {selectedTemplateConfig?.hasDaysInput && (
                                    <div className="flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
                                        <div className="w-24">
                                            <label className="text-xs font-bold text-purple-800 block mb-1">Dias Antes</label>
                                            <input 
                                                type="number" 
                                                min="0" 
                                                max="365"
                                                value={daysBefore} 
                                                onChange={e => setDaysBefore(e.target.value)} 
                                                className="w-full p-2 border rounded border-purple-200 text-center font-bold"
                                            />
                                        </div>
                                        <div className="text-xs text-purple-700 flex-1">
                                            <p>Defina com quantos dias de antecedência o aviso deve ser enviado.</p>
                                            <p className="mt-1 font-semibold">Ex: "3" enviará o aviso 3 dias antes do vencimento.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div>
                            <label className="text-sm font-medium">Título</label>
                            <input 
                                type="text" 
                                value={title} 
                                onChange={e => setTitle(e.target.value)} 
                                required 
                                disabled={isSystemTemplate}
                                className="w-full p-2 mt-1 border rounded disabled:bg-gray-100" 
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium">Mensagem</label>
                            <textarea 
                                value={message} 
                                onChange={e => setMessage(e.target.value)} 
                                required 
                                className="w-full p-2 mt-1 border rounded" 
                                rows="4"
                            ></textarea>
                            
                            {selectedTemplateConfig ? (
                                <p className="text-xs text-green-700 mt-1 bg-green-50 p-2 rounded border border-green-100">
                                    <strong>Variáveis disponíveis:</strong> {selectedTemplateConfig.variables}
                                </p>
                            ) : (
                                <p className="text-xs text-gray-500 mt-1">Escreva a mensagem que será enviada.</p>
                            )}
                        </div>
                        <div>
                            <label className="text-sm font-medium">Tipo de Alerta</label>
                            <select value={type} onChange={e => setType(e.target.value)} className="w-full p-2 mt-1 border rounded">
                                <option value="info">Info (Azul)</option>
                                <option value="success">Sucesso (Verde)</option>
                                <option value="warning">Atenção (Amarelo)</option>
                                <option value="error">Erro (Vermelho)</option>
                            </select>
                        </div>
                        {!isTemplate && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium">Data de Início</label>
                                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full p-2 mt-1 border rounded" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Data de Fim</label>
                                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full p-2 mt-1 border rounded" />
                                    <p className="text-xs text-gray-500 mt-1">Deixe em branco para nunca expirar.</p>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center justify-end p-4 bg-gray-50 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 mr-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                        <button type="submit" disabled={loading} className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-md disabled:bg-gray-400">
                            {loading ? 'A guardar...' : 'Guardar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Modal: Segmentação ---
const SegmentacaoModal = ({ aviso, avisoType, onClose, onSave, allUsers, allRoles }) => {
    const { apiFetch } = useApi();
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [selectedRoles, setSelectedRoles] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchTargets = async () => {
            try {
                const targets = await apiFetch(`/avisos/targets/${aviso.id}?type=${avisoType}`);
                setSelectedUsers(new Set(targets.filter(t => t.user_id).map(u => u.user_id)));
                setSelectedRoles(new Set(targets.filter(t => t.role_id).map(r => r.role_id)));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchTargets();
    }, [apiFetch, aviso.id, avisoType]);

    const handleUserToggle = (userId) => {
        setSelectedUsers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) newSet.delete(userId);
            else newSet.add(userId);
            return newSet;
        });
    };

    const handleRoleToggle = (roleId) => {
        setSelectedRoles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(roleId)) newSet.delete(roleId);
            else newSet.add(roleId);
            return newSet;
        });
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');
        try {
            await apiFetch(`/avisos/targets/${aviso.id}?type=${avisoType}`, {
                method: 'POST',
                body: {
                    userIds: Array.from(selectedUsers),
                    roleIds: Array.from(selectedRoles),
                }
            });
            onSave();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold">Segmentar Aviso</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X /></button>
                </div>
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                    {error && <p className="p-2 text-sm text-red-700 bg-red-100 rounded">{error}</p>}
                    {loading ? (
                        <p>A carregar segmentação...</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="font-semibold mb-2">Perfis de Utilizador (Envia para todos do perfil)</h3>
                                <div className="space-y-2 p-3 border rounded-md max-h-64 overflow-y-auto bg-gray-50">
                                    {allRoles.map(role => (
                                        <label key={role.id} className="flex items-center gap-2 p-2 rounded hover:bg-white cursor-pointer transition-colors">
                                            <input 
                                                type="checkbox"
                                                checked={selectedRoles.has(role.id)}
                                                onChange={() => handleRoleToggle(role.id)}
                                                className="w-4 h-4 text-blue-600 rounded"
                                            />
                                            {role.name}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3 className="font-semibold mb-2">Utilizadores Individuais</h3>
                                <div className="space-y-2 p-3 border rounded-md max-h-64 overflow-y-auto bg-gray-50">
                                    {allUsers.map(user => (
                                        <label key={user.id} className="flex items-center gap-2 p-2 rounded hover:bg-white cursor-pointer transition-colors">
                                            <input 
                                                type="checkbox"
                                                checked={selectedUsers.has(user.id)}
                                                onChange={() => handleUserToggle(user.id)}
                                                className="w-4 h-4 text-blue-600 rounded"
                                            />
                                            {user.username} <span className="text-xs text-gray-500">({user.roleName})</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end p-4 bg-gray-50 border-t">
                    <button type="button" onClick={onClose} className="px-4 py-2 mr-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                    <button type="button" onClick={handleSubmit} disabled={loading} className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-md disabled:bg-gray-400">
                        {loading ? 'A guardar...' : 'Guardar Segmentação'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Componente da Aba: Modelos ---
const TemplateManager = ({ apiFetch, currentUser, onEdit, onSegment, onDelete, onDataChange }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [verificando, setVerificando] = useState(false); 

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch('/avisos/templates');
            setTemplates(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleToggleActivate = async (template) => {
        const isActive = template.is_active === 1;
        const action = isActive ? 'Desativar' : 'Ativar';
        if (!window.confirm(`Tem a certeza que quer ${action.toLowerCase()} o modelo "${template.title}"?`)) return;
        
        try {
            await apiFetch(`/avisos/templates/${template.id}`, {
                method: 'PUT',
                body: { ...template, is_active: isActive ? 0 : 1 }
            });
            onDataChange(); 
        } catch (err) {
            setError(err.message);
        }
    };

    // --- BOTÃO DE VERIFICAÇÃO MANUAL ---
    const handleRunCheck = async () => {
        setVerificando(true);
        try {
            const res = await apiFetch('/avisos/check-financial', { method: 'POST' });
            alert(res.message || "Verificação concluída.");
            onDataChange(); 
        } catch (err) {
            alert(`Erro na verificação: ${err.message}`);
        } finally {
            setVerificando(false);
        }
    };
    
    const getTriggerLabel = (value) => {
        const t = TRIGGER_TEMPLATES[value];
        return t ? t.label : value;
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button 
                    onClick={handleRunCheck} 
                    disabled={verificando}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 disabled:opacity-50"
                >
                    <Zap size={14} />
                    {verificando ? 'Verificando...' : 'Rodar Verificação Financeira Agora'}
                </button>
            </div>

            {loading && <p>A carregar modelos...</p>}
            {error && <p className="text-red-600">{error}</p>}
            {!loading && templates.length === 0 && <p>Nenhum modelo de aviso criado.</p>}

            {templates.map(template => (
                <div key={template.id} className={`p-4 border-l-4 rounded-r-lg ${TIPO_AVISO_CLASSES[template.type]}`}>
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            {TIPO_AVISO_ICONE[template.type]}
                            <div>
                                <h3 className="font-bold flex items-center gap-2">
                                    {template.title}
                                    {template.trigger_event && (
                                        <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full border border-purple-200 flex items-center gap-1">
                                            <Zap size={10} /> {getTriggerLabel(template.trigger_event)}
                                            {template.days_before > 0 && (
                                                <span className="ml-1 bg-white px-1 rounded border border-purple-100 text-purple-800">
                                                    (-{template.days_before} dias)
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </h3>
                                <p className="text-sm mt-1">{template.message}</p>
                                {template.is_system === 1 && (
                                    <span className="text-xs font-semibold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full mt-2 inline-block">SISTEMA</span>
                                )}
                            </div>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-1">
                            <button 
                                onClick={() => handleToggleActivate(template)} 
                                title={template.is_active ? 'Desativar Automação' : 'Ativar Automação'} 
                                className={`p-2 rounded-full hover:bg-opacity-20 ${template.is_active ? 'text-green-600 hover:bg-green-200' : 'text-gray-500 hover:bg-gray-200'}`}
                            >
                                {template.is_active ? <Power size={18} /> : <PowerOff size={18} />}
                            </button>
                            
                            <button onClick={() => onSegment(template, 'template')} title="Definir Quem Recebe" className="p-2 rounded-full hover:bg-gray-500 hover:bg-opacity-20"><Users size={18} /></button>
                            
                            <button 
                                onClick={() => onEdit(template, true)} 
                                title="Editar" 
                                className="p-2 rounded-full hover:bg-gray-500 hover:bg-opacity-20"
                            >
                                <Edit size={18} />
                            </button>
                            
                            <button 
                                onClick={() => onDelete(template, true)} 
                                title="Excluir" 
                                className={`p-2 rounded-full ${template.is_system === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:bg-red-100'}`}
                                disabled={template.is_system === 1}
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    </div>
                    {template.is_active === 1 && (
                         <div className="text-xs text-green-700 font-semibold mt-2 pt-2 border-t border-green-200">
                             Ativo - Este aviso será gerado automaticamente para os alvos selecionados.
                         </div>
                    )}
                </div>
            ))}
        </div>
    );
};

// --- Componente da Aba: Avisos Ativos ---
const ActiveAvisosManager = ({ apiFetch, onEdit, onSegment, onDelete }) => {
    const [avisos, setAvisos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch('/avisos');
            setAvisos(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- CORREÇÃO DA DATA NOS ATIVOS ---
    // Adiciona o 'Z' para UTC se necessário
    const formatarData = (dateStr) => {
        if (!dateStr) return 'Permanente';
        const utcDate = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
        return new Date(utcDate).toLocaleDateString('pt-BR');
    };

    return (
        <div className="space-y-4">
            {loading && <p>A carregar avisos...</p>}
            {error && <p className="text-red-600">{error}</p>}
            {!loading && avisos.length === 0 && <p>Nenhum aviso manual ativo.</p>}
            
            {avisos.map(aviso => (
                <div key={aviso.id} className={`p-4 border-l-4 rounded-r-lg ${TIPO_AVISO_CLASSES[aviso.type]}`}>
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            {TIPO_AVISO_ICONE[aviso.type]}
                            <div>
                                <h3 className="font-bold">{aviso.title}</h3>
                                <p className="text-sm">{aviso.message}</p>
                                <div className="text-xs text-gray-600 mt-2 flex items-center gap-2">
                                    <Clock size={14} />
                                    <span>Início: {formatarData(aviso.start_date)}</span>
                                    <span>Fim: {formatarData(aviso.end_date)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-1">
                            <button onClick={() => onSegment(aviso, 'aviso')} title="Segmentar" className="p-2 rounded-full hover:bg-gray-500 hover:bg-opacity-20"><Users size={18} /></button>
                            <button onClick={() => onEdit(aviso, false)} title="Editar" className="p-2 rounded-full hover:bg-gray-500 hover:bg-opacity-20"><Edit size={18} /></button>
                            <button onClick={() => onDelete(aviso, false)} title="Excluir" className="p-2 text-red-600 rounded-full hover:bg-red-100"><Trash2 size={18} /></button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

// --- Componente da Aba: Histórico ---
const HistoryManager = ({ apiFetch }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch('/avisos/history');
            setHistory(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const formatHistoryTitle = (item) => {
        if (item.is_system_generated !== 1) return item.aviso_title;
        return item.aviso_title;
    };

    const renderHistoryStatus = (item) => {
        const today = new Date().toISOString().split('T')[0];
        if (item.status === 'read') return <span className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">Lido</span>;
        if (item.status === 'deleted') return <span className="text-xs text-red-700 bg-red-100 px-2 py-1 rounded-full">Excluído</span>;
        if (item.end_date && item.end_date < today) return <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">Expirado</span>;
        return <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">Não Lido</span>;
    };

    // --- CORREÇÃO DA DATA NO HISTÓRICO ---
    const renderHistoryDate = (item) => {
        if (item.status === 'read' && item.read_at) {
            // Garante UTC
            const dateString = item.read_at.endsWith('Z') ? item.read_at : item.read_at + 'Z';
            const readDate = new Date(dateString);
            
            // Converte para local
            const readDateStr = readDate.toLocaleString('pt-BR');
            
            if (item.end_date && readDate > new Date(item.end_date)) {
                return <span className="text-yellow-700 font-semibold">Lido após expirado ({readDateStr})</span>;
            }
            return readDateStr;
        }
        if (item.status === 'deleted') return 'Aviso excluído';
        const today = new Date().toISOString().split('T')[0];
        if (item.end_date && item.end_date < today) return 'Expirado';
        return 'N/A';
    };

    return (
        <div className="space-y-4">
            {loading && <p>A carregar histórico...</p>}
            {error && <p className="text-red-600">{error}</p>}
            {!loading && history.length === 0 && <p>Nenhum histórico de avisos encontrado.</p>}

            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="w-full text-sm text-left text-gray-600">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            <th className="px-6 py-3">Aviso (Título)</th>
                            <th className="px-6 py-3">Utilizador</th>
                            <th className="px-6 py-3">Status</th>
                            <th className="px-6 py-3">Data da Leitura</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.map(item => (
                            <tr key={item.id} className="bg-white border-b hover:bg-gray-50">
                                <td className="px-6 py-4 font-medium">{formatHistoryTitle(item)}</td>
                                <td className="px-6 py-4">{item.username}</td>
                                <td className="px-6 py-4">{renderHistoryStatus(item)}</td>
                                <td className="px-6 py-4">{renderHistoryDate(item)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// --- Componente Principal da Página ---
const AvisosPage = ({ currentUser }) => {
    const { apiFetch } = useApi();
    const [activeTab, setActiveTab] = useState('modelos');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTemplateMode, setIsTemplateMode] = useState(false);
    const [editingAviso, setEditingAviso] = useState(null);
    
    const [segmentingAviso, setSegmentingAviso] = useState({ aviso: null, type: null });
    const [isSegmentModalOpen, setIsSegmentModalOpen] = useState(false);
    const [loadingSegments, setLoadingSegments] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [allRoles, setAllRoles] = useState([]);
    
    const [dataChangeKey, setDataChangeKey] = useState(0); 

    const handleDataChange = () => setDataChangeKey(prev => prev + 1);

    const openModal = (aviso = null, isTemplate = false) => {
        setEditingAviso(aviso);
        setIsTemplateMode(isTemplate);
        setIsModalOpen(true);
    };

    const openSegmentModal = async (aviso, type) => {
        setLoadingSegments(true);
        setSegmentingAviso({ aviso, type });
        setIsSegmentModalOpen(true);
        try {
            const [usersData, rolesData] = await Promise.all([
                apiFetch('/users'),
                apiFetch('/roles')
            ]);
            setAllUsers(usersData);
            setAllRoles(rolesData);
        } catch (err) {
            console.error("Erro ao buscar dados para segmentação:", err);
            setIsSegmentModalOpen(false); 
        } finally {
            setLoadingSegments(false);
        }
    };
    
    const handleDelete = async (aviso, isTemplate) => {
        const typeText = isTemplate ? 'modelo' : 'aviso';
        if (isTemplate && aviso.is_system === 1) {
            alert("Não é possível excluir um template de sistema.");
            return;
        }
        
        const confirmText = isTemplate ? 
            `Tem a certeza que quer excluir este ${typeText}: "${aviso.title}"?` :
            `Tem a certeza que quer excluir (arquivar) este ${typeText}: "${aviso.title}"?\n\nIsto irá removê-lo dos "Avisos Ativos" e marcará como "excluído" no histórico de quem não leu.`;
        
        if (!window.confirm(confirmText)) return;
        
        const url = isTemplate ? `/avisos/templates/${aviso.id}` : `/avisos/${aviso.id}`;
        try {
            await apiFetch(url, { method: 'DELETE' });
            handleDataChange(); 
        } catch (err) {
            alert(`Erro ao excluir: ${err.message}`);
        }
    };
    
    const tabs = [
        { id: 'modelos', label: 'Modelos', icon: MessageSquare },
        { id: 'ativos', label: 'Avisos Ativos', icon: Bell },
        { id: 'historico', label: 'Histórico', icon: History }
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Gestão de Avisos</h1>
                <div className="flex gap-2">
                    <button onClick={() => openModal(null, false)} className="flex items-center gap-2 px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700">
                        <PlusCircle size={20} /> Criar Aviso Manual
                    </button>
                    <button onClick={() => openModal(null, true)} className="flex items-center gap-2 px-4 py-2 font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700">
                        <PlusCircle size={20} /> Criar Modelo
                    </button>
                </div>
            </div>

            <div className="flex border-b mb-6">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        <tab.icon size={16} /> {tab.label}
                    </button>
                ))}
            </div>

            <div className="p-1">
                {activeTab === 'ativos' && 
                    <ActiveAvisosManager 
                        key={`ativos-${dataChangeKey}`}
                        apiFetch={apiFetch} 
                        onEdit={openModal}
                        onSegment={openSegmentModal}
                        onDelete={handleDelete}
                    />}
                {activeTab === 'modelos' && 
                    <TemplateManager 
                        key={`modelos-${dataChangeKey}`}
                        apiFetch={apiFetch} 
                        currentUser={currentUser}
                        onEdit={openModal}
                        onSegment={openSegmentModal}
                        onDelete={handleDelete}
                        onDataChange={handleDataChange}
                    />}
                {activeTab === 'historico' && 
                    <HistoryManager 
                        key={`history-${dataChangeKey}`}
                        apiFetch={apiFetch} 
                    />}
            </div>
            
            {isModalOpen && (
                <AvisoFormModal
                    isTemplate={isTemplateMode}
                    avisoToEdit={editingAviso}
                    onClose={() => setIsModalOpen(false)}
                    onSave={() => {
                        setIsModalOpen(false);
                        handleDataChange();
                    }}
                />
            )}
            
            {isSegmentModalOpen && !loadingSegments && (
                <SegmentacaoModal
                    aviso={segmentingAviso.aviso}
                    avisoType={segmentingAviso.type}
                    onClose={() => setIsSegmentModalOpen(false)}
                    onSave={() => {
                        setIsSegmentModalOpen(false);
                        handleDataChange();
                    }}
                    allUsers={allUsers}
                    allRoles={allRoles}
                />
            )}
            
        </div>
    );
};

export default AvisosPage;